import React from 'react';

// Components
import { HistoricalScenariosFilterCreateCustomScenario, HistoricalScenariosFilterDropdownItems, HistoricalScenariosFilterEmptyResults } from './components';

// Libraries
import PerfectScrollbar from 'react-perfect-scrollbar';

// Types
import HistoricalScenario from '@/types/historical-scenario';
import Nullable from '@/types/nullable';
import User from '@/types/user';
import { UUID } from '@/types/global';

// Utils
import DropdownItem from '@/types/dropdown-item';
import DropdownOptionsGroup from '@/types/dropdown-options-group';
import { formatLocalizedString, formatMessage } from '@/utils/i18n';
import { PERMISSIONS, userHasPermissionEnabled } from '@/utils/permissions';

const MAX_VISIBLE_COUNT = 7;

interface HistoricalScenariosFilterOptionsProps {
  customScenarios?: HistoricalScenario[];
  onCreateScenario: () => void;
  onDeleteScenario: (id: UUID) => void;
  onEditScenario: (id: UUID) => void;
  onScenarioChange: (filter: string) => void;
  predefinedScenarios: HistoricalScenario[];
  search: string;
  selectedScenarios?: HistoricalScenario[];
  user?: Nullable<User>;
  readOnly?: boolean;
}

const HistoricalScenariosFilterOptions: React.FC<HistoricalScenariosFilterOptionsProps> = ({
  customScenarios,
  onCreateScenario,
  onDeleteScenario,
  onEditScenario,
  onScenarioChange,
  predefinedScenarios,
  search,
  selectedScenarios,
  user,
  readOnly,
}) => {
  const scenarioOptions = predefinedScenarios.map((scenario: HistoricalScenario) => ({
    value: scenario._id,
    label: formatLocalizedString(scenario.name),
  }));

  const optionsGroups: DropdownOptionsGroup[] = [];
  optionsGroups.push({
    label: '',
    options: scenarioOptions,
  });

  const customScenarioOptions = (customScenarios ?? []).map((scenario: HistoricalScenario) => ({
    value: scenario._id,
    label: formatLocalizedString(scenario.name),
    canDelete: true,
    canEdit: true,
  }));

  if (customScenarioOptions.length > 0) {
    optionsGroups.push({
      label: formatMessage('Search.Scenarios.CustomEvents'),
      options: customScenarioOptions,
    });
  }

  const getGroupItems = (group: DropdownOptionsGroup) =>
    group.options.filter(item => {
      if (search === '') return true;
      return (item.label || item.value.toString()).toLowerCase().indexOf(search.toLowerCase()) > -1;
    });

  const handleOnDropdownItemClick = (item: DropdownItem) => {
    handleOnToggleItem(item);
    if (item.onClick) {
      item.onClick();
    }
  };

  const handleOnToggleItem = (item: DropdownItem) => {
    onScenarioChange(item.value);
  };

  const validateEmptyResults = () => {
    if (search === '') return false;
    const availableOptions = optionsGroups.reduce<DropdownItem[]>((r, obj: DropdownOptionsGroup) => r.concat(obj.options), []);
    const filteredItems = availableOptions.filter(item => (item.label || item.value.toString()).toLowerCase().indexOf(search.toLowerCase()) > -1);
    return filteredItems.length === 0;
  };

  const validateIfItemIsSelected = (item: DropdownItem) => selectedScenarios?.some(sS => sS._id === item.value) || false;

  return (
    <>
      <PerfectScrollbar
        className="dropdown-options"
        options={{ suppressScrollX: true }}
        style={{ '--visible-count': MAX_VISIBLE_COUNT } as React.CSSProperties}
      >
        {optionsGroups.map((group, index) => {
          const items = getGroupItems(group);
          if (!items.length) return null;
          return (
            <div key={`optionsGroups-${index}`}>
              {group.label && <span className="options-group-label">{group.label}</span>}
              <HistoricalScenariosFilterDropdownItems
                items={items}
                onDeleteScenario={onDeleteScenario}
                onDropdownItemClick={handleOnDropdownItemClick}
                onEditScenario={onEditScenario}
                onToggleItem={handleOnToggleItem}
                validateIfItemIsSelected={validateIfItemIsSelected}
                readOnly={readOnly}
              />
            </div>
          );
        })}
        {validateEmptyResults() && <HistoricalScenariosFilterEmptyResults />}
      </PerfectScrollbar>

      {userHasPermissionEnabled(user, PERMISSIONS.HISTORICAL_SCENARIOS_CUSTOM_CREATION) && (
        <HistoricalScenariosFilterCreateCustomScenario totalCustomScenarios={customScenarioOptions.length} onCreateScenario={onCreateScenario} />
      )}
    </>
  );
};

export default HistoricalScenariosFilterOptions;
